import { computed, ref } from 'vue'

import type { Ref, ComputedRef } from 'vue'
import type { MultiselectSearchOption } from '@shared/ui/fields'
import type { KanbanAgent } from '@entities/task'

import { useProfileStore } from '@entities/viewer'

import { useTasksKanbanStore } from '../store'

interface FilterAssigneeOptions {
  initial: string[]
}

interface FilterAssigneeReturn {
  options: ComputedRef<MultiselectSearchOption[]>
  query: Ref<string[]>
  initialValue: ComputedRef<string[]>
  handleSelected: (selected: MultiselectSearchOption[]) => void
}

export const useFilterAssignee = ({ initial }: FilterAssigneeOptions): FilterAssigneeReturn => {
  const tasksKanbanStore = useTasksKanbanStore()
  const profileStore = useProfileStore()

  const query = ref<string[]>(initial)

  const options = computed<MultiselectSearchOption[]>(() => {
    const agents = tasksKanbanStore.getAgents
    const currentAdminId = profileStore.getUser?.id ?? null
    const names = agents.map((a: KanbanAgent) => a.name)

    const meAgent = currentAdminId !== null
      ? agents.find((a: KanbanAgent) => a.id === currentAdminId) ?? null
      : null

    const buildOption = (agent: KanbanAgent, isMe: boolean): MultiselectSearchOption => {
      const id = String(agent.id)
      const isDuplicatedName = names.filter((n: string) => n === agent.name).length > 1
      const baseText = isDuplicatedName ? `${agent.name} (id: ${id})` : agent.name
      const text = isMe ? `${baseText} (Me)` : baseText

      return {
        id,
        value: id,
        text,
        selected: query.value.includes(id),
      }
    }

    const restOptions = agents
      .filter((agent: KanbanAgent) => agent.id !== meAgent?.id)
      .map((agent: KanbanAgent) => buildOption(agent, false))

    const optionsArray = meAgent
      ? [buildOption(meAgent, true), ...restOptions]
      : restOptions

    optionsArray.splice(1, 0, {
      id: 'null',
      selected: query.value.includes('null'),
      text: 'Unassigned agent',
      value: 'null',
    })

    return optionsArray
  })

  const initialValue = computed<string[]>(() => {
    return options.value
      .filter(option => option.selected)
      .map(option => option.text)
  })

  const handleSelected = (selected: MultiselectSearchOption[]): void => {
    query.value = selected.map(option => String(option.id))
  }

  return {
    options,
    query,
    initialValue,
    handleSelected,
  }
}
